/*
 * Copyright (c) 2018 Bruce Schubert.
 * The MIT License
 * http://www.opensource.org/licenses/mit-license
 */
import React, {Component} from 'react'
import PropTypes from 'prop-types'
import Globe from '../globe'
import {Button, Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap'

import './SearchPreview.css'
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faSearch} from "@fortawesome/free-solid-svg-icons";

/* global WorldWind */

class SearchPreview extends Component {

    static propTypes = {
        title: PropTypes.string,
        mapQuestApiKey: PropTypes.string,
        isOpen: PropTypes.boolean,
        handleHideModal: PropTypes.func.isRequired,
        handleGotoSelection: PropTypes.func.isRequired,
    };

    static defaultProps = {
        title: 'Search Results',
    };

    constructor(props) {
        super(props);

        this.state = {
            showWarning: this.props.showApiWarning,
            selected: null,
            value: '',
            results: [],
            isLoading: false
        };

        this.handleGotoClick = this.handleGotoClick.bind(this);
        this.handlePreviewClick = this.handlePreviewClick.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.geocoder = new WorldWind.NominatimGeocoder();
        this.globeRef = React.createRef();

        this.globe = null;
        this.placemarkAttributes = new WorldWind.PlacemarkAttributes(null);
        this.placemarkAttributes.imageSource = "https://files.worldwind.arc.nasa.gov/artifactory/web/0.9.0/images/pushpins/castshadow-red.png";
        this.placemarkAttributes.imageScale = 0.5;
        this.placemarkAttributes.imageOffset = new WorldWind.Offset(
            WorldWind.OFFSET_FRACTION, 0.3,
            WorldWind.OFFSET_FRACTION, 0.0);
    }

    handleChange(event) {
        this.setState({value: event.target.value})
    }

    handleClick(event) {
        event.preventDefault();
        this.performSearch();
    }

    handleSubmit(event) {
        event.preventDefault();
        this.performSearch();
        return false;   // prevent page from refreshing
    }

    performSearch() {
        this.setState({isLoading: true});
        const globe = this.props.globe;

        if (!this.props.mapQuestApiKey) {
            console.error("SearchViewModel: A MapQuest API key is required to use the geocoder in production. Get your API key at https://developer.mapquest.com/")
        }

        const queryString = this.state.value;
        if (queryString) {
            if (queryString.match(WorldWind.WWUtil.latLonRegex)) {
                // Treat the text as a lat, lon pair
                let tokens = queryString.split(",");
                let latitude = parseFloat(tokens[0]);
                let longitude = parseFloat(tokens[1]);
                // Center the globe on the lat, lon
                globe.wwd.goTo(new WorldWind.Location(latitude, longitude))
            } else {
                // Treat the text as an address or place name
                let self = this;
                this.geocoder.lookup(queryString, function (geocoder, results) {
                    // Open the modal dialog to preview and select a result
                    // The modal is rendered when results > 0
                    self.setState({
                        results: results,
                        isLoading: false
                    })
                }, this.props.mapQuestApiKey)
            }
        }
    }

    handlePreviewClick(result) {
        if (this.globeRef.current && this.state.selection !== result) {
            let latitude = parseFloat(result.lat),
                longitude = parseFloat(result.lon);
            this.globeRef.current.goTo(latitude, longitude);
            // Update the selection used for the Go To button
            this.setState({
                selection: result
            });
        }
    }

    handleGotoClick() {
        // Call the parent's handler to process the selection
        this.props.handleGotoSelection(this.state.selection)
    }

    createMarker(result) {
        let marker = new WorldWind.Placemark(
            new WorldWind.Position(
                parseFloat(result.lat),
                parseFloat(result.lon), 100));
        marker.attributes = this.placemarkAttributes;
        marker.altitudeMode = WorldWind.RELATIVE_TO_GROUND;
        marker.displayName = result.display_name;
        return marker
    }

    /**
     * Updates the preview globe AFTER the modal is shown.
     * This cannot be done in componentDidMount because the modal is not being shown
     * at that time
     * @param {type} prevProps
     * @param {type} prevState
     */
    componentDidUpdate(prevProps, prevState) {
        if (this.globeRef.current) {
            const globe = this.globeRef.current;
            const resultsLayer = globe.addLayer('renderables');

            resultsLayer.removeAllRenderables();
            this.state.results.forEach((result) => {
                resultsLayer.addRenderable(this.createMarker(result))
            });
        }
    }

    render() {
        const {results, selection} = this.state;

        if (results.length > 0 && !selection) {
            this.handlePreviewClick(results[0]);
        }

        const tableRows = results.map((result) =>
            <tr key={result.place_id} className={result === this.state.selection ? "table-primary" : ""}
                onClick={() => this.handlePreviewClick(result)}>
                <td>{result.display_name}</td>
                <td>{result.type}</td>
            </tr>
        );

        return (
            <Modal className="search-modal" isOpen={this.props.isOpen} toggle={this.props.handleHideModal}>
                <ModalHeader toggle={this.props.handleHideModal}>{this.props.title}</ModalHeader>
                <ModalBody>
                    <input className="form-control mr-sm-2" type="text" placeholder="Search" aria-label="Search"
                           value={this.state.value} onChange={this.handleChange}/>
                    <Button color="success" onClick={this.handleClick}>
                        <FontAwesomeIcon icon={faSearch}/>
                    </Button>
                    <div className="preview-globe">
                        <Globe id="preview-globe" projection="Mercator" ref={this.globeRef}/>
                    </div>
                    {this.state.isLoading
                        ? "isLoading"
                        : (<div className="result-list">
                            <table className="table table-striped table-hover">
                                <thead>
                                <tr>
                                    <th scope="col">Name</th>
                                    <th scope="col">Type</th>
                                </tr>
                                </thead>
                                <tbody>
                                {tableRows}
                                </tbody>
                            </table>
                        </div>)
                    }
                </ModalBody>
                <ModalFooter>
                    <Button color="success" onClick={this.handleGotoClick} disabled={!this.state.selection}>Go To</Button>{' '}
                    <Button onClick={this.props.handleHideModal}>Cancel</Button>
                </ModalFooter>
            </Modal>
        );
    }
}

export default SearchPreview;
